/***
 * PROJECT-MESSAGE.WIDGET
 * Edit introduction message for the survey
 */

import { connect } from "react-redux"
import { WithTranslation, withTranslation } from "react-i18next"
import { Project, ProjectPopulationImageGroup, ProjectState } from "@/redux/project.types"
import { useEffect, useRef, useState } from "react"
import { PROJECT_FIELD_LANGUAGE_EVENT } from "@/utils/alert-target.utils"
import { store } from "@/index"
import {
  projectCheckMessage,
  projectEdit,
  projectEditMessageOptions,
  projectEditQrcodeOptions,
  projectUpdate
} from "@/redux/project.actions"
import { Session, SESSION_COLORS } from "@/redux/_session.types"
import { Language } from "@/redux/language.types"
import ModalConfirm from "@/components/modal-confirm"
import TextInput from "@/components/text-input"
import ConfirmButtons from "@/components/confirm-buttons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLanguage, faLink, faPen, faPlayCircle, faTrash } from "@fortawesome/free-solid-svg-icons"
import LanguageSelection from "@/components/language-selection"
import Space from "@/components/space"
import ContextMenu from "@/components/context-menu"
import TranslatorWidget from "./translator.widget"
import Link from "@/components/link"
import ProjectStepErrorsWidget from "./project-step-errors.widget"
import { TemplateState } from "@/redux/template.types"
import { toast } from "react-toastify"
import DuplicateTemplateAskModal from "@/modals/duplicate-template-ask.modal"
import ListItem from "@/components/list-item"
import Button from "@/components/button"
import Checkbox from "@/components/checkbox"
import ImageSelector from "@/components/image-selector"
import ColorPickerWidget from "./color-picker.widget"
import ImagePicker from "@/components/image-picker"
import CardButton from "@/components/card-button"
import PopulationImageGroupRow from "@/components/population-image-group-row"
import { NavigateFunction, useNavigate } from "react-router-dom"
import projectMessageInfo from "@/json/project-message-info.json"
import ProjectPopulationImagesModal from "@/modals/project-population-images.modal"

interface StateProps extends WithTranslation {
  _session: Session
  project: ProjectState
  template: TemplateState
}

interface OwnProps {
  onNext?: Function
  onEdit: Function
}

type Props = StateProps & OwnProps

const CONTEXT_AUTO_TRANSLATION: string = "CONTEXT_AUTO_TRANSLATION"
const CONTEXT_AUTO_TRANSLATION_CONFIRMATION: string = "CONTEXT_AUTO_TRANSLATION_CONFIRMATION"
const CONTEXT_AUTO_TRANSLATION_INFO: string = "CONTEXT_AUTO_TRANSLATION_INFO"
const MODAL_DUPLICATE_TEMPLATE: string = "MODAL_DUPLICATE_TEMPLATE"
const MODAL_EDIT_QRCODE: string = "MODAL_EDIT_QRCODE"
const MODAL_POPULATION_IMAGES: string = "MODAL_POPULATION_IMAGES"

function ProjectMessageWidget(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()

  const [currentLanguage, setCurrentLanguage] = useState(props.project.active.languages[0])

  //An alert of the step asks to show a language (the one where a text is missing)
  useEffect(() => {
    const onLanguage = (event: Event) => {
      const { language } = (event as CustomEvent<{ language: string }>).detail
      if (props.project.active.languages.includes(language)) setCurrentLanguage(language)
    }
    window.addEventListener(PROJECT_FIELD_LANGUAGE_EVENT, onLanguage)
    return () => window.removeEventListener(PROJECT_FIELD_LANGUAGE_EVENT, onLanguage)
  }, [props.project.active.languages])
  const [currentModal, setCurrentModal] = useState<string | null>(null)
  const [isChanged, setIsChanged] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [isVisited, setIsVisited] = useState(false)
  //Deliberately not seeded from (customImage && !image): auto-opening the file dialog on mount would fire the
  //native picker every time this step is (re)opened for a project that has customImage on but no image yet.
  //Only open it in response to an actual click (see the CardButton below).
  const [imageEdition, setImageEdition] = useState(false)
  const [isPopulationImageEnabled, setIsPopulationImageEnabled] = useState(
    props.project.active.populationImageGroups.length > 0
  )
  const imagePickerRef = useRef<HTMLDivElement>(null)
  const isEditingCustomImage = props.project.active.customImage && imageEdition

  //Open the native file dialog as soon as the picker shows up (skip the extra "Charger une image" click)
  //Scoped to our own picker (not document-wide) since the population-images modal can render another ImagePicker at the same time
  useEffect(() => {
    if (isEditingCustomImage) {
      const input = imagePickerRef.current?.querySelector<HTMLInputElement>("#avatar-" + props.project.active.id)
      const onCancel = () => setImageEdition(false)
      input?.addEventListener("cancel", onCancel)
      input?.click()
      return () => input?.removeEventListener("cancel", onCancel)
    }
  }, [isEditingCustomImage])

  function cancel() {
    store.dispatch(
      projectEdit(
        "messageOptions",
        new Project().getInitialMessageOptions(props._session.accountName, props.project.active.name)
      )
    )
    edit()
    next(false, false)
  }

  function edit() {
    store.dispatch(projectCheckMessage(props.project.active.languages, props.project.active.messageOptions))
    props.onEdit()
  }

  function editMessage(key: string, value: any) {
    store.dispatch(projectEditMessageOptions(key, value))
    setIsChanged(true)
    setTimeout(() => {
      edit()
    }, 200)
  }

  function editMessageText(key: string, value: string, language: string) {
    store.dispatch(
      projectEditMessageOptions(key, Object.assign({}, props.project.active.messageOptions[key], { [language]: value }))
    )
    setIsChanged(true)
  }

  function editQrcode() {
    store.dispatch(projectEditQrcodeOptions("content", props.project.active.messageOptions.content))
    nextConfirm(isVisited, isConfirmed)
  }

  function editTemplate() {
    if (props.template.active.fromSupervisor) {
      setCurrentModal(MODAL_DUPLICATE_TEMPLATE)
    } else if (props.template.active.id.length) {
      navigate("/survey/template")
    } else {
      toast(t("project_error_template_no_template"), { type: "error" })
    }
  }

  function next(isVisited: boolean, isConfirmed: boolean) {
    if (props.project.active.diffusionMode.qrcode && isChanged) {
      setIsVisited(isVisited)
      setIsConfirmed(isConfirmed)
      setCurrentModal(MODAL_EDIT_QRCODE)
    } else {
      nextConfirm(isVisited, isConfirmed)
    }
  }

  function nextConfirm(isVisited: boolean, isConfirmed: boolean) {
    if (props.onNext) {
      props.onNext(isVisited, isConfirmed)
    }
  }

  function copyPreviewLink() {
    navigator.clipboard.writeText(props.project.active.testTemplateLink)
    toast(t("utils_copy_link_done"))
  }

  //Save options
  //Timeout of 1000ms before save
  async function editProjectImage(save: boolean) {
    const response: any = await store.dispatch(projectUpdate(new Project({ ...props.project.active, image: save })))
    if (response.error) {
      toast(t("error_saving"), { type: "error" })
    } else {
      store.dispatch(projectEdit("image", save))
      setImageEdition(false)
    }
  }

  async function selectCustomImage(value: boolean) {
    const response: any = await store.dispatch(
      projectUpdate(new Project({ ...props.project.active, customImage: value }))
    )
    if (response.error) {
      toast(t("error_saving"), { type: "error" })
    } else {
      store.dispatch(projectEdit("customImage", value))
    }
  }

  async function savePopulationImageGroups(groups: ProjectPopulationImageGroup[]) {
    const response: any = await store.dispatch(
      projectUpdate(new Project({ ...props.project.active, populationImageGroups: groups }))
    )
    if (response.error) {
      toast(t("error_saving"), { type: "error" })
    } else {
      store.dispatch(projectEdit("populationImageGroups", groups))
    }
  }

  //Init message for the custom confirmation message
  function setConfirmationCustom() {
    if (!props.project.active.messageOptions.confirmationContent[currentLanguage]) {
      editMessageText(
        "confirmationContent",
        t("project_custom_info_message_confirm_text_placeholder", { accountName: props._session.accountName }),
        currentLanguage
      )
    }

    if (!props.project.active.messageOptions.confirmationTitle[currentLanguage]) {
      editMessageText("confirmationTitle", t("project_custom_info_message_confirm_title_placeholder"), currentLanguage)
    }

    if (!props.project.active.messageOptions.confirmationSender[currentLanguage]) {
      editMessageText("confirmationSender", props._session.accountName, currentLanguage)
    }

    editMessage("isConfirmationSend", !props.project.active.messageOptions.isConfirmationSend)
  }

  //Init message for the custom confirmation message
  function setInfoCustom() {
    if (!props.project.active.messageOptions.infoContent[currentLanguage]) {
      editMessage("infoContent", projectMessageInfo)
    }

    editMessage("isInfoCustom", !props.project.active.messageOptions.isInfoCustom)
  }

  //Default the source language to whichever has the most texts filled in for this section,
  //rather than always the project's first language - picking an empty/partial source would
  //translate blanks and purge the target language's existing content
  function findFilledSourceLanguage(sourceTexts: any[]): string {
    const languages: string[] = props.project.active.languages
    const filledCount = (lang: string) => sourceTexts.filter((text: any) => text && text[lang]).length

    return languages.reduce(
      (best: string, lang: string) => (filledCount(lang) > filledCount(best) ? lang : best),
      languages[0]
    )
  }

  //Open a translate popup, moving the current language tab off the detected source language
  //when they collide, so the target isn't preselected identical to the source
  function openTranslateModal(modalId: string, sourceLang: string) {
    if (currentLanguage === sourceLang) {
      const otherLanguage = props.project.active.languages.find((lang: string) => lang !== sourceLang)
      if (otherLanguage) setCurrentLanguage(otherLanguage)
    }

    setCurrentModal(modalId)
  }

  //Small standalone "Traductions" block (language chips + "Traduction automatique"), scoped to a single
  //section (its own source/target picker) so mixing up source and target only risks that section's
  //content, not everything at once
  function renderAutoTranslate(
    modalId: string,
    sourceTexts: any[],
    onTranslateTexts: (texts: string[], language: Language) => void
  ) {
    const sourceLang = findFilledSourceLanguage(sourceTexts)

    return (
      <div>
        <p className="grey-t">{t("utils_translations")}</p>

        <div className="flex">
          <LanguageSelection
            languages={props.project.active.languages}
            activeLang={currentLanguage}
            onClick={(language: Language) => setCurrentLanguage(language.code)}
          />

          <Space />

          <div className="rel">
            {props._session.isTranslationAllowed && (
              <span className="_hover grey-t cursor-pointer" onClick={() => openTranslateModal(modalId, sourceLang)}>
                <FontAwesomeIcon icon={faLanguage} style={{ marginRight: 6 }} />
                <u>{t("auto_translate")}</u>
              </span>
            )}

            {currentModal === modalId && (
              <ContextMenu positionRight onClose={() => setCurrentModal(null)}>
                <div style={{ padding: "10px 20px" }}>
                  <p>{t("auto_translate")}</p>

                  <TranslatorWidget
                    sourceLang={sourceLang}
                    targetLang={currentLanguage}
                    onTranslate={(texts: string[], language: Language) => {
                      onTranslateTexts(texts, language)
                      edit()
                      setCurrentModal(null)
                    }}
                    languages={props.project.active.languages}
                    sourceTexts={sourceTexts}
                    onTargetChange={(lang: Language) => setCurrentLanguage(lang.code)}
                  />
                </div>
              </ContextMenu>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {currentModal === MODAL_DUPLICATE_TEMPLATE && <DuplicateTemplateAskModal onClose={() => setCurrentModal(null)} />}

      {currentModal === MODAL_POPULATION_IMAGES && (
        <ProjectPopulationImagesModal
          groups={props.project.active.populationImageGroups}
          onClose={() => setCurrentModal(null)}
          onSave={(groups) => {
            savePopulationImageGroups(groups)
            setCurrentModal(null)
          }}
        />
      )}

      {currentModal === MODAL_EDIT_QRCODE && (
        <ModalConfirm
          onNo={() => nextConfirm(isVisited, isConfirmed)}
          onYes={editQrcode}
          textBold={t("project_message_edit_qrcode")}
        />
      )}

      <ListItem>
        <div className="setup-card-help">
          <p>{t("project_message_help")}</p>
          <ul className="grey-t setup-card-help-content">
            <li>{t("project_message_help_1")}</li>
            <li>
              {t("project_message_help_2")}
              &nbsp;
              <Link isWithoutMargin onClick={() => navigate("/adm/settings")}>
                {t("account_settings")}
              </Link>
            </li>
            <li>
              {t("project_message_help_3")}
              &nbsp;
              <Link isWithoutMargin onClick={() => editTemplate()}>
                {t("template_params")}
              </Link>
            </li>
          </ul>
        </div>
      </ListItem>

      {props.project.active.languages.length > 1 && (
        <ListItem>
          <div>
            <p className="grey-t">{t("utils_translations")}</p>

            <LanguageSelection
              languages={props.project.active.languages}
              activeLang={currentLanguage}
              onClick={(language: Language) => setCurrentLanguage(language.code)}
            />
          </div>

          <Space />

          <div className="flex flex-dcol">
            <Space />

            {props._session.isTranslationAllowed && (
              <span
                className="_hover grey-t cursor-pointer"
                onClick={() =>
                  openTranslateModal(
                    CONTEXT_AUTO_TRANSLATION,
                    findFilledSourceLanguage([
                      props.project.active.messageOptions.title,
                      props.project.active.messageOptions.content
                    ])
                  )
                }>
                <FontAwesomeIcon icon={faLanguage} style={{ marginRight: 6 }} />
                <u>{t("auto_translate")}</u>
              </span>
            )}

            <div className="rel">
              {currentModal === CONTEXT_AUTO_TRANSLATION && (
                <ContextMenu positionRight onClose={() => setCurrentModal(null)}>
                  <div style={{ padding: "10px 20px" }}>
                    <p>{t("auto_translate")}</p>

                    <TranslatorWidget
                      sourceLang={findFilledSourceLanguage([
                        props.project.active.messageOptions.title,
                        props.project.active.messageOptions.content
                      ])}
                      targetLang={currentLanguage}
                      onTranslate={(texts: string[], language: Language) => {
                        editMessageText("title", texts[0], language.code)
                        editMessageText("content", texts[1], language.code)
                        edit()
                        setCurrentModal(null)
                      }}
                      languages={props.project.active.languages}
                      sourceTexts={[
                        props.project.active.messageOptions.title,
                        props.project.active.messageOptions.content
                      ]}
                      onTargetChange={(lang: Language) => setCurrentLanguage(lang.code)}
                    />
                  </div>
                </ContextMenu>
              )}
            </div>
          </div>
        </ListItem>
      )}

      <div style={{ padding: 32 }}>
        <Checkbox
          active={props.project.active.messageOptions.isWelcomeHidden}
          onClick={() => editMessage("isWelcomeHidden", !props.project.active.messageOptions.isWelcomeHidden)}
          text={t("project_welcome_hidden")}
        />

        <div className="height-20" />

        <div className="flex">
          <div className="flex1">
            <TextInput
              id="message-title"
              title={t("project_message_title")}
              value={props.project.active.messageOptions.title[currentLanguage] ?? ""}
              onBlur={edit}
              onChange={(e) => editMessageText("title", e.value, currentLanguage)}
            />

            {!props.project.active.messageOptions.isWelcomeHidden && (
              <div className="flex">
                <div style={{ margin: "4px 0px" }}>
                  <Checkbox
                    text={t("project_message_title_secondary")}
                    active={props.project.active.messageOptions.isWithTitleSecondary}
                    onClick={() =>
                      editMessage("isWithTitleSecondary", !props.project.active.messageOptions.isWithTitleSecondary)
                    }
                  />
                </div>

                <div className="width-20" />

                {props.project.active.messageOptions.isWithTitleSecondary && (
                  <TextInput
                    value={props.project.active.messageOptions.titleSecondary[currentLanguage] ?? ""}
                    onBlur={edit}
                    onChange={(e) => editMessageText("titleSecondary", e.value, currentLanguage)}
                  />
                )}
              </div>
            )}
          </div>

          <div style={{ marginLeft: 14, textAlign: "center" }}>
            <div className="_hover rel" onClick={() => window.open(props.project.active.testTemplateLink, "_blank")}>
              <div
                className="abs"
                style={{
                  textAlign: "center",
                  backgroundColor: "#0000006b",
                  color: "white",
                  margin: "7px 10px",
                  borderRadius: 2,
                  width: 180,
                  height: 88,
                  paddingTop: 50
                }}>
                <div>
                  <b>{t("utils_preview")}</b>
                </div>
                <FontAwesomeIcon icon={faPlayCircle} />
              </div>

              <img src={require("@/assets/qv.png")} style={{ marginBottom: 6 }} alt="qv" width={200} />
            </div>
            <Link icon={faLink} isWithoutMargin onClick={copyPreviewLink}>
              {t("utils_copy_link")}
            </Link>
          </div>
        </div>

        <div className="height-10" />

        <div className="grey-t">{t("project_invitation_content")}</div>

        <textarea
          id="message-content"
          style={{
            resize: "none",
            height: "300px",
            width: "calc(100% - 20px)",
            border: "none",
            padding: "6px 10px",
            backgroundColor: "#eaeaea",
            borderRadius: "8px",
            margin: "12px 0px"
          }}
          onChange={(e) => editMessageText("content", e.target.value, currentLanguage)}
          onBlur={edit}
          value={
            props.project.active.messageOptions.content[currentLanguage]
              ? props.project.active.messageOptions.content[currentLanguage]
              : ""
          }></textarea>

        <div className="height-20" />

        <div className="flex rel">
          <div>
            {!props.project.active.messageOptions.isWelcomeHidden && (
              <>
                <p className="grey-t">{t("project_message_layout")}</p>

                <div className="height-10" />

                <div className="flex">
                  <ImageSelector
                    onClick={() => editMessage("isAlternativeLayout", false)}
                    width={280}
                    height={180}
                    texts={[
                      { value: t("project_message_layout_title"), top: 16, left: 62 },
                      { value: t("project_message_layout_logo"), top: 80, left: 202 },
                      { value: t("project_message_layout_text"), top: 92, left: 62 }
                    ]}
                    isActive={!props.project.active.messageOptions.isAlternativeLayout}
                    imageName="project-layout-1"
                  />

                  <div className="width-20" />

                  <ImageSelector
                    onClick={() => editMessage("isAlternativeLayout", true)}
                    width={280}
                    height={180}
                    texts={[
                      { value: t("project_message_layout_title"), top: 20, left: 86 },
                      { value: t("project_message_layout_logo"), top: 20, left: 222 },
                      { value: t("project_message_layout_text"), top: 106, left: 120 }
                    ]}
                    isActive={props.project.active.messageOptions.isAlternativeLayout}
                    imageName="project-layout-2"
                  />
                </div>

                <div className="height-20" />
              </>
            )}

            <div>
              <p className="grey-t">{t("project_image_description")}</p>

              <div className="height-10" />

              <div className="flex card-container">
                {props.template.active.imageUrl && (
                  <CardButton
                    title={t("project_image_template")}
                    image={props.template.active.imageUrl}
                    isActive={!props.project.active.customImage}
                    onClick={() => props.project.active.customImage && selectCustomImage(false)}
                  />
                )}

                {isEditingCustomImage ? (
                  <div ref={imagePickerRef} className="flex flex-dcol" style={{ width: 140, margin: 14 }}>
                    <ImagePicker
                      id={props.project.active.id}
                      square
                      imageId={props.project.active.image ? props.project.active.id : null}
                      model="Projects"
                      onDelete={() => editProjectImage(false)}
                      onSave={() => editProjectImage(true)}
                    />

                    <Space />

                    <div className="grey-t flex">
                      <Space />
                      <u className="_hover grey-t" onClick={() => setImageEdition(false)}>
                        {t("utils_cancel")}
                      </u>
                      <Space />
                    </div>
                  </div>
                ) : (
                  <CardButton
                    title={t("project_image_custom")}
                    image={props.project.active.imageUrl}
                    isActive={props.project.active.customImage}
                    onClick={() => {
                      if (!props.project.active.customImage) {
                        selectCustomImage(true)
                      }
                      if (!props.project.active.image) {
                        setImageEdition(true)
                      }
                    }}
                    actions={
                      props.project.active.customImage && props.project.active.image
                        ? [
                            { id: "edit", icon: faPen, label: t("utils_edit") },
                            { id: "delete", icon: faTrash, label: t("utils_delete") }
                          ]
                        : undefined
                    }
                    onAction={(actionId) => {
                      if (actionId === "edit") setImageEdition(true)
                      if (actionId === "delete") editProjectImage(false)
                    }}
                  />
                )}
              </div>
            </div>

            <div className="height-20" />

            <div>
              <Checkbox
                active={isPopulationImageEnabled}
                onClick={() => setIsPopulationImageEnabled(!isPopulationImageEnabled)}
                text={t("project_image_population_enable")}
              />

              {isPopulationImageEnabled && (
                <div>
                  <div className="height-10" />

                  {props.project.active.populationImageGroups.length > 0 && (
                    <div
                      className="list-container"
                      style={{ border: "1px solid #ececec", borderRadius: 8, overflow: "hidden" }}>
                      {props.project.active.populationImageGroups.map((group) => (
                        <ListItem key={group.id}>
                          <PopulationImageGroupRow group={group} />
                        </ListItem>
                      ))}
                    </div>
                  )}

                  <div className="height-20" />

                  <Button className="light" isFullWidth onClick={() => setCurrentModal(MODAL_POPULATION_IMAGES)}>
                    {t("project_image_population_add")}
                  </Button>
                </div>
              )}
            </div>

            {isPopulationImageEnabled && <div className="height-20" />}

            <Checkbox
              active={props.project.active.messageOptions.displayImage}
              onClick={() => editMessage("displayImage", !props.project.active.messageOptions.displayImage)}
              text={t("project_image_display")}
            />

            <Checkbox
              active={props.project.active.messageOptions.isInfoCustom}
              onClick={() => setInfoCustom()}
              text={t("project_custom_info_message")}
            />
          </div>

          <div className="width-20" />

          <div className="flex1" />

          {!props.project.active.messageOptions.isWelcomeHidden && (
            <div>
              <ColorPickerWidget
                title={t("project_color_background")}
                selectedColor={
                  props.project.active.messageOptions.backgroundColor
                    ? props.project.active.messageOptions.backgroundColor
                    : props._session.accountColors.brandPrimary
                }
                colors={SESSION_COLORS}
                isCustom
                onSelect={(color) => editMessage("backgroundColor", color)}
              />

              <ColorPickerWidget
                title={t("project_color_text")}
                selectedColor={
                  props.project.active.messageOptions.textColor
                    ? props.project.active.messageOptions.textColor
                    : props._session.accountColors.brandText
                }
                colors={SESSION_COLORS}
                isCustom
                onSelect={(color) => editMessage("textColor", color)}
              />

              <ColorPickerWidget
                title={t("project_color_button")}
                selectedColor={
                  props.project.active.messageOptions.buttonColor
                    ? props.project.active.messageOptions.buttonColor
                    : props._session.accountColors.cta
                }
                colors={SESSION_COLORS}
                isCustom
                onSelect={(color) => editMessage("buttonColor", color)}
              />

              <ColorPickerWidget
                title={t("project_color_background_image")}
                selectedColor={
                  props.project.active.messageOptions.backgroundImageColor
                    ? props.project.active.messageOptions.backgroundImageColor
                    : props.project.active.messageOptions.backgroundColor
                      ? props.project.active.messageOptions.backgroundColor
                      : props._session.accountColors.brandPrimary
                }
                colors={SESSION_COLORS}
                isCustom
                onSelect={(color) => editMessage("backgroundImageColor", color)}
              />
            </div>
          )}
        </div>

        {props.project.active.messageOptions.isInfoCustom && (
          <div>
            <div className="height-20" />

            {props.project.active.messageOptions.isWelcomeHidden && (
              <p className="orange-t">{t("project_welcome_hidden_info_warning")}</p>
            )}

            <Link
              isWithoutMargin
              onClick={() =>
                window.open(
                  "https://docs.github.com/fr/get-started/writing-on-github/getting-started-with-writing-and-formatting-on-github/basic-writing-and-formatting-syntax",
                  "_blank"
                )
              }>
              {t("project_custom_info_message_help")}
            </Link>

            {props.project.active.languages.length > 1 && (
              <div>
                <div className="height-20" />

                {renderAutoTranslate(
                  CONTEXT_AUTO_TRANSLATION_INFO,
                  [props.project.active.messageOptions.infoContent],
                  (texts, language) => editMessageText("infoContent", texts[0], language.code)
                )}
              </div>
            )}

            <div className="height-10" />

            <textarea
              id="message-info"
              style={{
                resize: "none",
                height: "400px",
                width: "calc(100% - 20px)",
                border: "none",
                padding: "6px 10px",
                backgroundColor: "#eaeaea",
                borderRadius: "8px",
                margin: "12px 0px",
                overflowY: "auto"
              }}
              onChange={(e) => editMessageText("infoContent", e.target.value, currentLanguage)}
              onBlur={edit}
              value={
                props.project.active.messageOptions.infoContent[currentLanguage]
                  ? props.project.active.messageOptions.infoContent[currentLanguage]
                  : ""
              }></textarea>
          </div>
        )}

        <Checkbox
          active={props.project.active.messageOptions.isConfirmationSend}
          onClick={() => setConfirmationCustom()}
          text={t("project_custom_info_message_confirm")}
        />

        {props.project.active.messageOptions.isConfirmationSend && (
          <div>
            <div className="height-20" />

            <div className="flex">
              <TextInput
                id="message-confirmation-title"
                title={t("project_message_title")}
                value={props.project.active.messageOptions.confirmationTitle[currentLanguage] ?? ""}
                onBlur={edit}
                onChange={(e) => editMessageText("confirmationTitle", e.value, currentLanguage)}
              />

              <div className="width-20" />

              <TextInput
                title={t("utils_from")}
                value={props.project.active.messageOptions.confirmationSender[currentLanguage] ?? ""}
                onBlur={edit}
                onChange={(e) => editMessageText("confirmationSender", e.value, currentLanguage)}
              />
            </div>

            {props.project.active.languages.length > 1 && (
              <div>
                <div className="height-10" />

                {renderAutoTranslate(
                  CONTEXT_AUTO_TRANSLATION_CONFIRMATION,
                  [
                    props.project.active.messageOptions.confirmationTitle,
                    props.project.active.messageOptions.confirmationContent
                  ],
                  (texts, language) => {
                    editMessageText("confirmationTitle", texts[0], language.code)
                    editMessageText("confirmationContent", texts[1], language.code)

                    //Sender is a name, not translated content - just copied over if missing for this language
                    if (!props.project.active.messageOptions.confirmationSender[language.code]) {
                      editMessageText("confirmationSender", props._session.accountName, language.code)
                    }
                  }
                )}
              </div>
            )}

            <div className="height-10" />

            <p className="grey-t">
              <b>{t("project_custom_info_message_confirm_text")}</b>
            </p>

            <textarea
              id="message-confirmation-content"
              style={{
                resize: "none",
                height: "120px",
                width: "calc(100% - 20px)",
                border: "none",
                padding: "6px 10px",
                backgroundColor: "#eaeaea",
                borderRadius: "8px",
                margin: "12px 0px",
                overflowY: "auto"
              }}
              onChange={(e) => editMessageText("confirmationContent", e.target.value, currentLanguage)}
              onBlur={edit}
              value={
                props.project.active.messageOptions.confirmationContent[currentLanguage]
                  ? props.project.active.messageOptions.confirmationContent[currentLanguage]
                  : ""
              }></textarea>
          </div>
        )}

        {/* QUICK PATCH : récapitulatif masqué (page résultats désactivée côté qv)
        {props._session.connectedAsSupervisor && (
          <Checkbox
            active={props.project.active.messageOptions.isSummaryAvailable}
            onClick={() => {
              if (!props.project.active.messageOptions.summaryContent[currentLanguage]) {
                editMessage("summaryContent", { [currentLanguage]: t("project_summary_available_default") })
              }
              editMessage("isSummaryAvailable", !props.project.active.messageOptions.isSummaryAvailable)
            }}
            text={t("project_summary_available")}
          />
        )}

        {props.project.active.messageOptions.isSummaryAvailable && (
          <div>
            <div className="height-20" />

            <p className="grey-t">
              <b>{t("project_summary_available_text")}</b>
            </p>

            <textarea
              id="message-summary"
              style={{
                resize: "none",
                height: "120px",
                width: "calc(100% - 20px)",
                border: "none",
                padding: "6px 10px",
                backgroundColor: "#eaeaea",
                borderRadius: "8px",
                margin: "12px 0px",
                overflowY: "auto"
              }}
              onChange={(e) => editMessageText("summaryContent", e.target.value, currentLanguage)}
              onBlur={edit}
              value={
                props.project.active.messageOptions.summaryContent[currentLanguage]
                  ? props.project.active.messageOptions.summaryContent[currentLanguage]
                  : ""
              }></textarea>
          </div>
        )} */}
      </div>

      <ProjectStepErrorsWidget alert={props.project.active.alerts.message} step="message" />

      {props.onNext && (
        <ConfirmButtons
          onCancel={() => cancel()}
          onConfirm={() => next(true, true)}
          onConfirmLater={() => next(true, false)}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  project: state.project,
  template: state.template
})

export default connect(mapStateToProps)(withTranslation()(ProjectMessageWidget))
